import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const PointsInfoContext = createContext({
  openPointsInfo: () => {},
});

export function usePointsInfo() {
  return useContext(PointsInfoContext);
}

/** @param {HTMLElement} anchorEl */
function computePopoverPosition(anchorEl) {
  const margin = 12;
  const maxW = Math.min(320, window.innerWidth - 2 * margin);
  const estimatedMaxH = Math.min(520, window.innerHeight - 2 * margin);
  const isRtl = document.documentElement.lang === "ar";

  const card = anchorEl.closest(".emp-score-card");
  if (card) {
    const r = card.getBoundingClientRect();
    let top = r.top + 16;
    let left = isRtl ? r.left + 16 : r.right - maxW - 16;
    left = Math.min(Math.max(margin, left), window.innerWidth - maxW - margin);
    if (top + estimatedMaxH > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - margin - estimatedMaxH);
    }
    return { top, left, width: maxW };
  }

  const r = anchorEl.getBoundingClientRect();
  let top = r.bottom + 8;
  let left = isRtl ? r.left : r.right - maxW;
  left = Math.min(Math.max(margin, left), window.innerWidth - maxW - margin);
  if (top + estimatedMaxH > window.innerHeight - margin) {
    top = Math.max(margin, r.top - estimatedMaxH - 8);
  }
  return { top, left, width: maxW };
}

function resolveAnchorElement(anchorOrEvent) {
  if (!anchorOrEvent) return null;
  if (anchorOrEvent.currentTarget instanceof HTMLElement) {
    return anchorOrEvent.currentTarget;
  }
  if (anchorOrEvent instanceof HTMLElement) {
    return anchorOrEvent;
  }
  return null;
}

function PointsHowItWorksPanel({ onClose }) {
  const { t } = useTranslation(["common"]);

  return (
    <div className="points-how-it-works-popover__inner">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <p id="points-how-it-works-heading" className="mb-0 font_14 font-bold">
          {t("pointsInfoTitle")}
        </p>
        <button
          type="button"
          className="emp-score-card__info-close-btn"
          onClick={onClose}
          aria-label={t("closePointsInfo")}
        >
          <i className="fas fa-times"></i>
        </button>
      </div>
      <p className="mb-2 font_13 font-color-grey">{t("pointsInfoDescription")}</p>
      <ul className="emp-score-card__points-rules mb-0">
        <li>{t("pointsRuleDailyLogin")}</li>
        <li>{t("pointsRuleCourseCompleted")}</li>
        <li>{t("pointsRuleVideoQuiz")}</li>
        <li>{t("pointsRuleLessonQuiz")}</li>
        <li>{t("pointsRuleLearningTrackCompleted")}</li>
      </ul>
    </div>
  );
}

export function PointsInfoProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [coords, setCoords] = useState(null);
  const popoverRef = useRef(null);

  const closePointsInfo = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
    setCoords(null);
  }, []);

  const openPointsInfo = useCallback((anchorOrEvent) => {
    const el = resolveAnchorElement(anchorOrEvent);
    if (!el) return;
    setAnchorEl(el);
    setCoords(computePopoverPosition(el));
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open || !anchorEl) return undefined;

    const update = () => {
      setCoords(computePopoverPosition(anchorEl));
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorEl]);

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closePointsInfo();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, closePointsInfo]);

  useEffect(() => {
    if (!open) return undefined;

    const onMouseDown = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      closePointsInfo();
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, anchorEl, closePointsInfo]);

  const value = useMemo(() => ({ openPointsInfo }), [openPointsInfo]);

  const popoverStyle =
    coords &&
    open && {
      top: coords.top,
      left: coords.left,
      width: coords.width,
    };

  return (
    <PointsInfoContext.Provider value={value}>
      {children}
      {open &&
        coords &&
        createPortal(
          <div
            ref={popoverRef}
            className="points-how-it-works-popover"
            style={popoverStyle}
            role="dialog"
            aria-modal="false"
            aria-labelledby="points-how-it-works-heading"
          >
            <PointsHowItWorksPanel onClose={closePointsInfo} />
          </div>,
          document.body,
        )}
    </PointsInfoContext.Provider>
  );
}
